import React from 'react';
import { Link } from 'react-router-dom';
import SeoLandingLayout from '../../components/SeoLandingLayout/SeoLandingLayout';
import {
  IA_BRAND_NAME,
  IA_CASOS,
  IA_HUB,
  IA_SERVICES,
  buildIaBreadcrumb,
  buildIaServiceSchema,
} from '../../config/iaBrand';

const SERVICE = IA_SERVICES.cumplimiento;

const FAQ = [
  {
    question: '¿El Artículo 4 del AI Act aplica a pymes que solo usan ChatGPT?',
    answer:
      'Sí, si tu personal usa sistemas de IA en el trabajo. El Artículo 4 exige alfabetización en IA proporcional al riesgo y al uso. No hace falta ser una big tech: hace falta saber qué herramientas usáis, quién las usa y con qué reglas.',
  },
  {
    question: '¿La formación bonificada por FUNDAE tiene coste para la empresa?',
    answer:
      'El plan de alfabetización se puede diseñar para bonificación FUNDAE. En la práctica, muchas pymes lo asumen a coste cero o casi cero si la bonificación encaja. Lo revisamos en el diagnóstico.',
  },
  {
    question: '¿Qué documentación pide una inspección de la AESIA?',
    answer:
      'Como mínimo: inventario de usos de IA, política de uso escrita, evidencia de formación y responsables. Nosotros dejamos ese paquete listo para auditoría, sin alarmismo y sin papeleo inútil.',
  },
];

const CumplimientoIAPage = ({ darkMode, toggleDarkMode }) => (
  <SeoLandingLayout
    darkMode={darkMode}
    toggleDarkMode={toggleDarkMode}
    title="Cumple el Artículo 4 del AI Act antes de la inspección de la AESIA | E-ASY"
    description="La AESIA empieza a inspeccionar el 2 de agosto de 2026. Diagnóstico, formación bonificada FUNDAE y documentación lista para auditoría. Sin coste para tu empresa."
    keywords="cumplir artículo 4 AI Act, formación IA obligatoria empresa, AESIA inspección alfabetización IA, curso IA bonificado FUNDAE, sanciones AI Act pymes España"
    canonical={SERVICE.url}
    h1="Cumple el Artículo 4 del AI Act antes de que la AESIA inspeccione"
    lede="El 2 de agosto de 2026 la AESIA gana capacidad real de inspección y sanción. La mayoría de pymes usa IA a diario y no tiene nada documentado. Te ayudamos a ordenarlo sin drama."
    ctaTo="/contacto"
    ctaLabel="Diagnóstico gratuito de 20 minutos"
    footerCtaLabel="Diagnóstico gratuito de 20 minutos"
    faqItems={FAQ}
    serviceJsonLd={buildIaServiceSchema({
      name: 'Cumplimiento y alfabetización IA (Artículo 4 AI Act)',
      description:
        'Auditoría de uso de IA, formación bonificable FUNDAE, política de uso y documentación lista para inspección AESIA.',
      url: SERVICE.url,
      serviceType: 'Cumplimiento normativo y formación en inteligencia artificial',
    })}
    serviceJsonLdId="jsonld-service-cumplimiento-ia"
    cobertura
    breadcrumbJsonLd={buildIaBreadcrumb([
      { name: IA_HUB.label, url: IA_HUB.url },
      { name: SERVICE.label, url: SERVICE.url },
    ])}
  >
    <h2>El problema</h2>
    <p>
      ChatGPT, Copilot, asistentes en el CRM o plantillas con IA ya están en el día a día.
      El <strong>Artículo 4 del AI Act</strong> exige que las organizaciones aseguren un nivel adecuado de{' '}
      <strong>alfabetización en IA</strong> de su personal. No es un trámite abstracto: la{' '}
      <strong>AESIA</strong> podrá inspeccionar y sancionar. Sin inventario, sin política y sin evidencia de
      formación, una pyme queda expuesta aunque “solo use herramientas de terceros”.
    </p>
    <p>
      No hace falta alarmismo. Hace falta un plan corto, proporcional y defendible ante auditoría.
    </p>

    <h2>La solución</h2>
    <p>
      En {IA_BRAND_NAME} convertimos el cumplimiento del Artículo 4 en un trabajo concreto: qué IA usáis,
      quién la usa, qué riesgos hay y qué documentación deja la empresa preparada. Si encaja, la formación
      se estructura para <strong>bonificación FUNDAE</strong> y el coste para el cliente puede quedar en cero.
    </p>

    <h2>Qué incluye</h2>
    <ul>
      <li>Auditoría de uso de IA en la empresa (inventario de herramientas y personas)</li>
      <li>Plan y formación de alfabetización, bonificable por FUNDAE (coste cero para el cliente cuando aplica)</li>
      <li>Documentación lista para una inspección de la AESIA</li>
      <li>Política de uso de IA por escrito</li>
    </ul>

    <h2>Por qué con E-ASY</h2>
    <p>
      No somos una gestoría genérica de “cursos de moda”. Conectamos cumplimiento con la realidad tecnológica
      de tu empresa: sistemas, datos y el resto de servicios de IA (
      <Link to={IA_SERVICES.agentes.path}>{IA_SERVICES.agentes.shortLabel}</Link>,{' '}
      <Link to={IA_SERVICES.privada.path}>{IA_SERVICES.privada.shortLabel}</Link>
      {' '}y{' '}
      <Link to={IA_SERVICES.produccion.path}>{IA_SERVICES.produccion.shortLabel}</Link>
      ). Si ya trabajas con nosotros en ciberseguridad o software, el diagnóstico no parte de cero.
    </p>
    <p>
      Relacionado:{' '}
      <Link to="/servicios/ciberseguridad-cumplimiento">ciberseguridad y cumplimiento</Link>.
    </p>

    <h2>Siguiente paso</h2>
    <p>
      Empieza por un diagnóstico de 20 minutos. Sin compromiso y con foco en lo mínimo que debes tener listo
      antes de agosto de 2026.
    </p>
    <p>
      <Link to="/contacto" className="seo-landing-cta">
        Diagnóstico gratuito de 20 minutos
      </Link>
    </p>
    <p>
      Lee también:{' '}
      <Link to={`/blog/${SERVICE.blogSlug}`}>
        Qué exige realmente el Artículo 4 del AI Act a una pyme canaria
      </Link>
      . Casos de entidades donde ya trabajamos con datos sensibles:{' '}
      <Link to={IA_CASOS.path}>{IA_CASOS.label}</Link>.
    </p>
  </SeoLandingLayout>
);

export default CumplimientoIAPage;
